package com.htmlagent.site.state.brief

import androidx.compose.runtime.Stable
import com.htmlagent.site.i18n.BriefStrings
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow

/**
 * State of the commission assistant: whether it is open, which question is showing, and what
 * has been gathered.
 *
 * It talks to nothing. The assistant produces text and the chat sends it, so there is no client
 * here, no endpoint and nothing to fail — which is also why the whole feature lives in `:site`.
 *
 * `@Stable` is a promise, and the three public properties are `StateFlow`s with no plain field
 * among them, which is what makes it true: without it, every keystroke in the free-text field
 * would recompose the workspace behind the dialog along with the dialog itself.
 */
@Stable
class BriefPresenter {

    private val _visible = MutableStateFlow(false)

    /** Whether the assistant is on screen. */
    val visible: StateFlow<Boolean> = _visible.asStateFlow()

    private val _step = MutableStateFlow(BriefStep.Commission)

    /** Which of the four questions is showing. */
    val step: StateFlow<BriefStep> = _step.asStateFlow()

    private val _draft = MutableStateFlow(Brief())

    /** Everything gathered so far, including which steps were left open. */
    val draft: StateFlow<Brief> = _draft.asStateFlow()

    /**
     * Opens it, carrying over whatever was already typed in the composer.
     *
     * Somebody who wrote "una landing para mi cafetería" and then opened the assistant should
     * not have to type it again: that sentence is the commission, and asking for it twice is the
     * fastest way to make an assistant feel like paperwork.
     */
    fun show(typed: String = "") {
        if (typed.isNotBlank() && _draft.value.what.isBlank()) {
            _draft.value = _draft.value.copy(what = typed.trim())
        }
        _step.value = BriefStep.Commission
        _visible.value = true
    }

    /** Closes it without discarding anything: reopening resumes where it was left. */
    fun hide() {
        _visible.value = false
    }

    /** Jumps to a question from the step rail. */
    fun goTo(step: BriefStep) {
        _step.value = step
    }

    /** The next question, or this one when it is the last. */
    fun next() {
        _step.value = _step.value.next
    }

    /** The previous one, or this one when it is the first. */
    fun back() {
        _step.value = _step.value.previous
    }

    /** Leaves the current question alone and moves on. What it would have said, the agent says. */
    fun skip() {
        val current = _step.value
        _draft.value = _draft.value.leaveOpen(current)
        _step.value = current.next
    }

    /** Every choice goes through here, and every choice opts its own step back in. */
    fun edit(transform: Brief.() -> Brief) {
        _draft.value = _draft.value.transform()
    }

    /** The message that goes to the agent. Reading it does not close the assistant. */
    fun prompt(strings: BriefStrings): String = _draft.value.toPrompt(strings)

    /** Back to an empty draft, for a session that starts over. */
    fun reset() {
        _draft.value = Brief()
        _step.value = BriefStep.Commission
        _visible.value = false
    }
}
